import React, { useState } from 'react';
import { ChevronDown, ListChecks } from 'lucide-react';

export const AGENDA_TITLE = 'Sample Session Agenda (90 minutes)';
export const AGENDA_NOTE = 'Suggested structure only. Mentors may adapt it to the career and participants.';

export const SESSION_AGENDA = [
  { duration: '10 min', segment: 'Introductions & Icebreaker', description: 'Mentor introduction, student interests and expectations' },
  { duration: '15 min', segment: 'My Career Journey', description: 'Education, career choices, experiences and turning points' },
  { duration: '20 min', segment: 'A Day in My Career', description: 'Responsibilities, actual work, challenges and rewards' },
  { duration: '15 min', segment: 'Skills, Subjects & Career Pathways', description: 'Relevant subjects, qualifications, skills and alternative routes' },
  { duration: '15 min', segment: 'Real-World Experience', description: 'Projects, demonstrations, examples or practical scenarios' },
  { duration: '10 min', segment: 'Student Q&A', description: 'Questions about the career and profession' },
  { duration: '5 min', segment: 'Wrap-Up', description: 'Key takeaways, advice and next steps' },
];

/** One rule for every role: the agenda is a planning aid, so it shows while an online
 *  experience is still ahead and disappears once it is over or called off. */
const AGENDA_HIDDEN_STATUSES = ['awaiting_feedback', 'completed', 'cancelled', 'rejected', 'not_conducted', 'declined'];
export const showAgendaFor = (status) => !AGENDA_HIDDEN_STATUSES.includes(status);

/** The shared sample agenda for online career exploration sessions. Read-only. */
export function SessionAgenda({ defaultOpen = false, compact = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={compact ? 'rounded-xl border border-[#E5E7EB] bg-[#FBFBFC]' : 'ce-card p-5'} data-testid="session-agenda">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-start gap-2.5 text-left ${compact ? 'p-3' : ''}`}
        data-testid="session-agenda-toggle"
      >
        <ListChecks className="h-4 w-4 text-[#F97D6A] shrink-0 mt-0.5" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-[#1F2937] font-['Outfit']" data-testid="session-agenda-title">
            {AGENDA_TITLE}
          </span>
          <span className="block text-xs text-[#6B7280] mt-0.5 leading-relaxed" data-testid="session-agenda-note">
            {AGENDA_NOTE}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-[#9CA3AF] shrink-0 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={compact ? 'px-3 pb-3 space-y-2' : 'mt-3 space-y-2'} data-testid="session-agenda-list">
          {SESSION_AGENDA.map(item => (
            <div
              key={item.segment}
              className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-3 rounded-lg bg-white sm:bg-[#F8F9FA] px-3 py-2 border border-[#EFF0F2] sm:border-0"
              data-testid={`agenda-item-${item.segment.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <span className="text-xs font-bold text-[#F97D6A] sm:w-16 shrink-0">{item.duration}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#1F2937]">{item.segment}</span>
                <span className="block text-xs text-[#6B7280] leading-relaxed">{item.description}</span>
              </span>
            </div>
          ))}
          <p className="text-xs text-[#9CA3AF] pt-0.5" data-testid="session-agenda-total">Total: 90 minutes.</p>
        </div>
      )}
    </div>
  );
}

export default SessionAgenda;
